import { CommonModule } from '@angular/common';
import { Component, Injector, Input, ViewChild } from '@angular/core';
import { LocalizationService, LocalizationModule } from '@abp/ng.core';
import {
  CellValueChangedEvent,
  ColDef,
  GetContextMenuItemsParams,
  MenuItemDef,
} from '@ag-grid-community/core';
import { EMPTY, Observable } from 'rxjs';
import { BaseThirdPartyDocLineEditor, LinesEditorComponent } from '../../../../components';
import {
  CustomerReturnNoteDto,
  CustomerReturnNoteService,
  DocLineRole,
  DocLineUpdatedDto,
} from '@digiwall/goliath-documents/proxy';
import { VatType } from '@digiwall/goliath-core/proxy';
import { ObserverQueue } from '@digiwall/core';
import { DocType } from '../../../base-docs/detail/doc-type';

@Component({
  selector: 'document-customer-return-note-lines-grid',
  templateUrl: './customer-return-note-lines-editor.component.html',
  imports: [CommonModule, LinesEditorComponent, LocalizationModule],
  providers: [ObserverQueue],
})
export class CustomerReturnNoteLinesEditorComponent extends BaseThirdPartyDocLineEditor<CustomerReturnNoteDto> {
  @ViewChild('lineGrid') lineGrid: LinesEditorComponent;

  protected articleLineRole: DocLineRole = DocLineRole.CustomerReturnNoteArticle;
  protected textLineRole: DocLineRole = DocLineRole.CustomerDocText;
  protected chapterLineRole: DocLineRole = DocLineRole.DocChapter;

  @Input() set doc(value) {
    this._document = value;
    this.setLanguageCode();
  }

  @Input() set readonly(value) {
    this._readonly = value;
  }

  protected override articleLookupFn = (term: string) =>
    this.articleService.getArticleSellableSelectionLookup(term);

  public columnDefs: Array<ColDef> = [];

  constructor(
    localizationService: LocalizationService,
    private customerReturnNoteService: CustomerReturnNoteService,
    injector: Injector,
    updateQueue: ObserverQueue,
  ) {
    super(localizationService, customerReturnNoteService, injector, updateQueue);

    this.initColDefs();
  }

  private initColDefs(): void {
    this.columnDefs = super.colDefs();
    this.columnDefs.push(
      ...([
        this.colDef_Quantity,
        this.colDef_ArticleSalesUnitCode(DocType.Sales),
        this.colDef_UnitPrice,
        ...this.getColDef_StockColumns(),
        this.colDef_Discount,
        this.colDef_VatCode(VatType.Sales),
        this.colDef_TotalPrice,
        this.colDef_UnitWeight,
        this.getColdef_StockLocation(DocType.Sales, false),
      ] as Array<ColDef>),
    );
    this.markDimensionFieldsAsNonEditable(this.columnDefs);
  }

  override isChapter = line => line.lineRole == this.chapterLineRole;

  protected getContextMenuItems = (params: GetContextMenuItemsParams): (string | MenuItemDef)[] => {
    return this.contextMenuItems(params);
  };

  protected getCellValueChangedObservable(
    event: CellValueChangedEvent<any, any>,
  ): Observable<DocLineUpdatedDto> {
    let observable: Observable<DocLineUpdatedDto> = super.getCellValueChangedObservable(event);
    if (observable == EMPTY) {
      switch (event.column.getColId()) {
        case 'unitPrice':
          observable = this.customerReturnNoteService.setLineUnitPrice(
            this._document.id,
            this._document.concurrencyStamp,
            event.data.id,
            event.data.unitPrice,
          );
          break;
        case 'articleStockUnitPrice':
          observable = this.customerReturnNoteService.setLineStockUnitPrice(
            this._document.id,
            this._document.concurrencyStamp,
            event.data.id,
            event.data.articleStockUnitPrice,
            true,
          );
          break;
        case 'discount':
          observable = this.customerReturnNoteService.setLineDiscount(
            this._document.id,
            this._document.concurrencyStamp,
            event.data.id,
            event.data.discount,
          );
          break;
        case 'unitWeight':
          observable = this.customerReturnNoteService.setLineUnitWeight(
            this._document.id,
            this._document.concurrencyStamp,
            event.data.id,
            event.data.unitWeight,
          );
          break;
        default:
          observable = EMPTY;
          break;
      }
    }
    return observable;
  }

  protected onCellValueChanged = (
    event: CellValueChangedEvent<any, any>,
  ): void | PromiseLike<void> => {
    let obs: Observable<DocLineUpdatedDto> = this.getCellValueChangedObservable(event);

    this._updateQueue
      .enqueue(() => obs)
      .subscribe({
        next: response => {
          if (response) this._updateLines(response);
        },
        error: _ => {
          let oldData = event.data;
          oldData[event.colDef.field] = event.oldValue;
          this.lineGrid.grid.grid.api.applyTransaction({
            update: [oldData],
          });
        },
      });
  };
}
